import React from "react";
// nodejs library that concatenates classes
import classNames from "classnames";

// @mui/icons-material
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Quote from "components/Typography/Quote.js";
import styles from "../../../../services/ServicesSections.module.scss";
// core components





export default function SectionText() {
  const classes = styles;
  const imgClasses = classNames(
    classes.imgRaised,
    classes.imgRounded,
    classes.imgFluid
  );
  return (
    <div className={classes.section} id = "start">
      <GridContainer justifyContent="center">
        <GridItem xs={12} sm={10} md={10}>

        <h2 className={classes.title}> Using Streamlit To Build Web Applications</h2>
          <h3 className={classes.title} id="summary">What is Streamlit?</h3>
            <p>Streamlit is an open-source and free Python library for making custom web applications.
              One can literally build and deploy an app in several minutes - as <a href="https://streamlit.io/">Streamlit creators say.</a></p>

            <p> Figure below highlights minimum requirements for creating and running a Streamlit application.</p>

              <div style={{textAlign: "center",maxWidth:"1000px",width:"100%"}}>
            <GridItem xs={25} sm={25} md={25}>     
                  <img
                    src="/img/posts/2024-01-15-using-streamlit-to-build-web-applications/streamlit_in_5_steps.png"
                    alt="Building a Streamlit application in 5 steps."
                    className={imgClasses}
                  />
                  <p><br></br><i>Slide from my talk on Streamlit at Measurecamp showing how to create a web application in 5 steps. 
                    You will need to have Python and Streamlit, for which installations depends on your system. Once set up, create and run simple python file as shown.</i></p>
            </GridItem>
            </div>


          <h3 className={classes.title} id="summary">Why use Streamlit?</h3>

            <p> With Streamlit everyone who knows Python can create interactive, fast, visually pleasing and customised web applications. 
              If you already have Python scripts that do data analysis, or create reports or do any kind of calculations or visualisations, 
              following Streamlit documentation you can seamlessly add its components and turn your scripts into interactive web applications.  </p>

          
        
              <div style={{textAlign: "center",maxWidth:"1000px",width:"100%"}}>
            <GridItem xs={25} sm={25} md={25}>     
                  <img
                    src="/img/posts/2024-01-15-using-streamlit-to-build-web-applications/streamlit_input_widgets.png"
                    alt="Example of Streamlit widgets."
                    className={imgClasses}
                  />
                  <p><br></br><i>Slide from my talk on Streamlit at Measurecamp showing several Streamlit widgets that are controlling data: slider, button, checkbox, radio button, select box and number input.</i></p>
            </GridItem>
            </div>


            <h3 className={classes.title} id="summary">When to use Streamlit?</h3>

              <p> I would say: always :) </p>

              <p> In reality - I recommend to clients Streamlit when I know that, for example,
               Looker Studio will not be good enough for their needs and when Looker Studio is slow. Don’t get me wrong, 
               I like Looker Studio - but it is often limited in speed and complexity. </p>


               <p> <b>With Streamlit, you can create a tool that: </b> </p>
                <li>Is built specifically for you; </li>
                <li>Is tailored to your business questions;</li>
                <li>Offers in-depth performance analysis;</li>
                <li>Supports interactive exploration;</li>
                <li>It is easy and fast to use.</li>

                <h3 className={classes.title} id="summary">How to create and deploy web applications with Streamlit?</h3>

                <p>Start from the beginning. Install Streamlit, set up your working space, and create a layout and plan of what you want to create. </p>

                <p>Once you have a working application - even if it’s just the application name and nothing else, you can use the free Streamlit Community Cloud to deploy your application. 
                  5 steps to deploy a Streamlit application are shown on the Figure below. </p>

                  <p> Whether you are making a private or public web application - you will need a GitHub account and you will need to keep your code there. 
              Once your application is deployed, you can then share its URL with others (publicly or privately): </p>
              <li>Public app - just share URL and people can access your app.</li>
              <li>Private app - you will need to add emails to your Streamlit secrets and then only those people can see your app.</li>
              <p></p>

                <div style={{textAlign: "center",maxWidth:"1000px",width:"100%"}}>
            <GridItem xs={25} sm={25} md={25}>     
                  <img
                    src="/img/posts/2024-01-15-using-streamlit-to-build-web-applications/streamlit_deployment.png"
                    alt="Steps to deploy Streamlit app on a Community Cloud."
                    className={imgClasses}
                  />
                  <p><br></br><i>Slide from my talk on Streamlit at Measurecamp showing how to deploy Streamlit applications to their Community Cloud. 
                    You will need Community Cloud account and your code needs to be on GitHub. </i></p>
            </GridItem>
            </div>




              <h3 className={classes.title} id="summary">My personal and public Streamlit applications</h3>

              <p>Over time, since getting to know about Streamlit in 2021, I’ve created quite a few applications - some are publicly available and some are private to clients. </p>

              <p>You can access my publicly available web applications here:</p>
              <li><a href="https://share.streamlit.io/rdzudzar/distributionanalyser/main/main.py">Distribution Analyser</a> - Explore and fit continuous distribution. </li>
              <li><a href="https://color-extractor.streamlit.app/">Color Extractor</a> - Explore and extract pre-made and custom colormaps for any use, and swatches files for Procreate drawing app.</li>
              <li><a href="https://hi-rings.streamlit.app/">HI-RINGS</a> - Interactively explore a multiwavelength sample of Ring galaxies and their HI content. </li>



            <h4 className={classes.title} id="summary">Distribution Analyser</h4>

              <p> Distribution Analyser is a web application that allows you to interactively explore continuous distributions, fit distribution(s) to your data and take home more than just a figure. </p>

              <p> I recommend this app to all people, whether they are new to distributions, or experts, whether they are students or teachers, because:  </p>
                <li>People can get familiar with a particular distribution very quickly and easily through the interaction with distribution parameters.</li>
                <li>People can quickly see which distributions fit to their data, narrowing down the number of distributions they need to use for their analysis.</li>
                <p></p>
                <div style={{textAlign: "center",maxWidth:"1000px",width:"100%"}}>
            <GridItem xs={25} sm={25} md={25}>     
                  <img
                    src="/img/posts/2024-01-15-using-streamlit-to-build-web-applications/distribution_analyser.png"
                    alt="Components for tracking Sessions and Page Views - real and modelled data."
                    className={imgClasses}
                  />
                  <p><br></br><i>Screenshot of one part of the Streamlit web application ‘Distribution Analyser’. 
                    UI is set to a left-hand side, while the right side display results.</i></p>
            </GridItem>
            </div>

              

</GridItem>





          

      </GridContainer>
    </div>
  );
}
